import React, { useState, useEffect } from "react";
import { Input, Form, Col, Row, Button, Select, Tooltip, Modal } from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import config from "util/ApiforContext";
import {
  defaultValidateMessages,
  onInputMaxLengthCheck,
  onKeyDownNumberOnly,
} from "./Helper";
import TextArea from "antd/lib/input/TextArea";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const METHOD_TYPE_MAP = {
  sftp: "FTP",
  mail: "EMAIL",
  webService: "WEBSERVICE",
  pdf: "PDF",
};

const TosBankInfoAddForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [activeBankId, setActiveBankId] = useState();
  const [form] = Form.useForm();

  const [connectionType, setConnectionType] = useState("none");
  const [requirements, setRequirements] = useState([]);
  const [activeRequirement, setActiveRequirement] = useState(null);

  const [isSupportwebService, setIsSupportwebService] = useState(false);
  const [isSupportSftp, setIsSupportSftp] = useState(false);
  const [isSupportMail, setIsSupportMail] = useState(false);
  const [isSupportPdf, setIsSupportPdf] = useState(false);
  const [keyModalVisible, setKeyModalVisible] = useState(false);

  useEffect(() => {
    getNotAddedBanks();
  }, []);

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue(props.data);
    }
  }, [props.data]);

  const getNotAddedBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });

    RequestHandler.get(`/Bank/GetTosBankDropdown`).then((response) => {
      setBankSelect({
        data: response.filter((x) => x.id !== 999),
        loading: false,
      });
    });
  };

  const onFinish = (values) => {
    setLoading(true);
    if (values.connectionType === "mail") {
      values.IsUseEmail = true;
    }

    if (values.connectionType === "webService") {
      values.IsUseWebService = true;
    }

    if (values.connectionType === "pdf") {
      values.IsUsePdf = true;
    }

    const methodType = METHOD_TYPE_MAP[values.connectionType];
    const req = requirements.find((r) => r.methodType === methodType);

    if (req) {
      if (!req.isClientIdRequired) delete values.clientId;
      if (!req.isClientSecretRequired) delete values.clientSecret;
      if (!req.isClientCertificateRequired) delete values.clientCertificate;
      if (!req.isCorporateCodeRequired) delete values.corporateCode;
      if (!req.isCustomerNumberRequired) delete values.customerNumber;
      if (!req.isHostRequired) delete values.host;
      if (!req.isPortRequired) delete values.port;
      if (!req.isDownloadFolderRequired) delete values.downloadFolder;
      if (!req.isUploadFolderRequired) delete values.uploadFolder;
      if (!req.isUserNameRequired) delete values.userName;
      if (!req.isPasswordRequired) delete values.password;
      if (!req.isRecipientEmailRequired) delete values.emailAddresses;
    }

    delete values.connectionType;

    RequestHandler.post(`/TosBankInfo`, values).then((response) => {
      setLoading(false);
      if (response.success) {
        getNotAddedBanks();
        form.resetFields();
        setConnectionType("none");
        setActiveRequirement(null);
        if (props.onSuccess) props.onSuccess();
      }
    });
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
  };

  const onBankChange = (bankId) => {
    setActiveBankId(bankId);

    var bank = bankSelect.data.filter((x) => x.id === bankId)[0];

    setIsSupportSftp(bank.isTosSupport);
    setIsSupportwebService(bank.isOnlineTosSupport);
    setIsSupportMail(bank.isMailTosSupport);
    setIsSupportPdf(bank.isTosSupportPdf);

    setConnectionType("none");
    setActiveRequirement(null);
    form.resetFields([
      "connectionType",
      "clientId",
      "clientSecret",
      "userName",
      "password",
      "corporateCode",
      "customerNumber",
      "host",
      "port",
      "downloadFolder",
      "uploadFolder",
      "emailAddresses",
      "clientCertificate",
    ]);

    RequestHandler.get(`/TosBankInfo/GetBankRequirements/${bankId}`).then(
      (response) => {
        setRequirements(response || []);
      }
    );
  };

  const onConnectionTypeChange = (type) => {
    const methodType = METHOD_TYPE_MAP[type];
    const req = requirements.find((r) => r.methodType === methodType);
    setActiveRequirement(req || null);

    if (type === "sftp" && req) {
      if (req.serviceDefaultHost || req.serviceDefaultPort) {
        form.setFieldsValue({
          port: req.serviceDefaultPort,
          host: req.serviceDefaultHost,
        });
      } else {
        form.resetFields(["port", "host"]);
      }
    }

    if (req?.isKeyRequired) {
      setKeyModalVisible(true);
    }

    setConnectionType(type);
  };

  const downloadKeyFile = () => {
    window.open(
      `${config.apiUrl}/TenantKeyFile/DownloadPublicKey/TosOnline/${activeBankId}?t=${RequestHandler.getToken()}`
    );
  };

  const FormItemLabelWithTooltip = ({ label, tooltip }) => {
    return (
      <>
        {label}{" "}
        <Tooltip
          title={
            <span>
              {tooltip
                ?.split("*")
                .filter((part) => part.trim() !== "")
                .map((line, index) => (
                  <div key={index}> * {line}</div>
                ))}
            </span>
          }
          overlayStyle={{ maxWidth: "450px" }}
        >
          <QuestionCircleOutlined
            className="gx-text-blue"
            style={{ marginLeft: 5, marginRight: 3, fontSize: "120%" }}
          />
        </Tooltip>
      </>
    );
  };

  const req = activeRequirement;

  return (
    <>
    <Form
      {...formItemLayout}
      validateMessages={defaultValidateMessages}
      form={form}
      name="tosBankInfoAdd"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Row>
        <Col span={24}>
          <Form.Item name="bankId" label="Banka" rules={[{ required: true }]}>
            <Select
              allowClear
              showSearch
              placeholder="Seçiniz"
              onChange={onBankChange}
              filterOption={(input, option) =>
                option.children
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
              loading={bankSelect.loading}
            >
              {bankSelect.data.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="Tanım Adı"
            rules={[{ required: true, whitespace: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="connectionType" label="Bağlantı Tipi">
            <Select placeholder="Seçiniz" onChange={onConnectionTypeChange}>
              {isSupportSftp ? (
                <Select.Option key="sftp" value="sftp">
                  Sftp
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportMail ? (
                <Select.Option key="mail" value="mail">
                  Mail
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportwebService ? (
                <Select.Option key="webService" value="webService">
                  Web Servis
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportPdf ? (
                <Select.Option key="pdf" value="pdf">
                  Pdf
                </Select.Option>
              ) : (
                <></>
              )}
            </Select>
          </Form.Item>

          {req && (
            <>
              {req.isCustomerNumberRequired && (
                <Form.Item
                  label={req.customerNumberLabel || "Müşteri Numarası"}
                  name="customerNumber"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.customerNumberLabel || "Müşteri Numarası"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input maxLength="125" onInput={onInputMaxLengthCheck} />
                </Form.Item>
              )}

              {req.isCorporateCodeRequired && (
                <Form.Item
                  label={req.corporateCodeLabel || "Kurumsal Kod"}
                  name="corporateCode"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.corporateCodeLabel || "Kurumsal Kod"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isHostRequired && req.isPortRequired && (
                <Form.Item style={{ marginBottom: 0 }} label="Host" required>
                  <Input.Group>
                    <Row gutter={2}>
                      <Col span={18}>
                        <Form.Item
                          name="host"
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "'Host' alanı gerekli.",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name="port"
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "'Port' alanı gerekli.",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Port"
                            onKeyDown={(e) => onKeyDownNumberOnly(e)}
                            maxLength="5"
                            onInput={onInputMaxLengthCheck}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Input.Group>
                </Form.Item>
              )}

              {req.isDownloadFolderRequired && (
                <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label="Gelen Dosya Yolu"
                      tooltip="destek@finekra.com mail adresine sorarak öğreniniz."
                    />
                  }
                  name="downloadFolder"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isUploadFolderRequired && (
                <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label="Giden Dosya Yolu"
                      tooltip="destek@finekra.com mail adresine sorarak öğreniniz."
                    />
                  }
                  name="uploadFolder"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isUserNameRequired && (
                <Form.Item
                  label={req.userNameLabel || "Kullanıcı Adı"}
                  name="userName"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.userNameLabel || "Kullanıcı Adı"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isPasswordRequired && (
                <Form.Item
                  label={req.passwordLabel || "Şifre"}
                  name="password"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.passwordLabel || "Şifre"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              )}

              {req.isClientIdRequired && (
                <Form.Item
                  label={req.clientIdLabel || "Client Id"}
                  name="clientId"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.clientIdLabel || "Client Id"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isClientSecretRequired && (
                <Form.Item
                  label={req.clientSecretLabel || "Client Secret"}
                  name="clientSecret"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.clientSecretLabel || "Client Secret"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isClientCertificateRequired && (
                <Form.Item
                  label="Sertifika"
                  name="clientCertificate"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "'Sertifika' alanı gerekli.",
                    },
                  ]}
                >
                  <TextArea rows={10} />
                </Form.Item>
              )}

              {req.isRecipientEmailRequired && (
                <Form.Item
                  label="Alıcı E-postaları"
                  name="emailAddresses"
                  rules={[
                    {
                      validator: (rule, values) => {
                        if (!values || values.length === 0) {
                          return Promise.reject(
                            new Error("Alıcı e-posta adresleri boş bırakılamaz.")
                          );
                        }

                        const emailRegex =
                          /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,6})$/;
                        const invalidInputs = values.filter(
                          (value) => !value.match(emailRegex)
                        );
                        if (invalidInputs.length === 0) {
                          return Promise.resolve();
                        }

                        if (invalidInputs.length === 1) {
                          return Promise.reject(
                            new Error(
                              `'${invalidInputs.join(
                                ""
                              )}' geçersiz bir e-postadır.`
                            )
                          );
                        } else {
                          return Promise.reject(
                            new Error(
                              `'${invalidInputs.join(
                                ", "
                              )}' değerleri geçersiz e-postadır.`
                            )
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    tokenSeparators={[",", ";"]}
                    mode="tags"
                    placeholder={
                      "Talimat girilen mail adreslerine gönderilir."
                    }
                  ></Select>
                </Form.Item>
              )}
            </>
          )}

          <Col style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              htmlType="submit"
              loading={loading}
            >
              Ekle
            </Button>
          </Col>
        </Col>
      </Row>
    </Form>
    <Modal
      title="Sertifika Dosyası"
      visible={keyModalVisible}
      onOk={() => setKeyModalVisible(false)}
      onCancel={() => setKeyModalVisible(false)}
      centered={true}
      keyboard={true}
      width={500}
    >
      <p>
        Banka entegrasyon sürecinizin tamamlanabilmesi için sertifika dosyasını
        banka sistemine yüklemeniz gerekmektedir.
      </p>
      <p>
        Sertifika dosyasını{" "}
        <span
          onClick={downloadKeyFile}
          style={{ color: "blue", cursor: "pointer" }}
        >
          buradan{" "}
        </span>
        indirebilirsiniz
      </p>
      <p>
        Herhangi bir sorunla karşılaşırsanız, lütfen teknik destek ekibimizle
        iletişime geçin.
      </p>
    </Modal>
    </>
  );
};
export default TosBankInfoAddForm;
